import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import componentStyles from '../../styles/component.styles.js';
import styles from './fade.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';

/** Layout snapshot taken during the read phase of a batched measurement. */
interface FadeMetrics {
  scrollPos: number;
  viewport: number;
  content: number;
}

// Overflow detection is done off the hot render path. Reading layout
// (scrollWidth/clientWidth) synchronously inside each cell's render/`updated`
// interleaves reads with the DOM writes of sibling cells, forcing one reflow per
// cell - which made a large faded table (hundreds of cells) thrash layout for
// seconds. Instead every fade that needs (re)measuring is queued, and a single
// rAF pass reads ALL of them first and then writes ALL of them, so the browser
// does one layout for the whole batch. A single shared ResizeObserver (rather
// than one per instance) re-queues a fade when its box changes size.
const measureQueue = new Set<WebmoduleFade>();
let measureScheduled = false;

function runMeasurePass() {
  measureScheduled = false;
  const fades = [...measureQueue];
  measureQueue.clear();
  // Read all, then write all - never interleave, so it stays a single reflow.
  const metrics = fades.map(f => f.readMetrics());
  fades.forEach((f, i) => f.applyEdges(metrics[i]));
}

function scheduleMeasure(fade: WebmoduleFade) {
  measureQueue.add(fade);
  if (measureScheduled) return;
  measureScheduled = true;
  requestAnimationFrame(runMeasurePass);
}

const fadeByContainer = new WeakMap<Element, WebmoduleFade>();

const sharedResizeObserver =
  typeof ResizeObserver === 'undefined'
    ? null
    : new ResizeObserver(entries => {
        for (const entry of entries) {
          const owner = fadeByContainer.get(entry.target);
          if (owner) scheduleMeasure(owner);
        }
      });

/**
 * @summary Wraps overflowing content in a scrollable region with a soft fade at
 * the scrollable edges. Long values stay on a single line (or inside a capped
 * block) and scroll, instead of wrapping - a middle ground between hard
 * truncation and full wrapping.
 *
 * @slot - The content to contain. In the default horizontal orientation it is
 * kept on one line and scrolls sideways; in vertical orientation it wraps and
 * scrolls within a capped height.
 *
 * @csspart base - The scroll container that holds the content and carries the fade.
 *
 * @cssproperty --fade-size - Length of the fade cue at each scrollable edge.
 * @cssproperty --max-block-size - Height cap that creates the vertical scroll region (orientation="vertical").
 *
 * @tag webmodule-fade
 */
export default class WebmoduleFade extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('.fade') private scrollContainer: HTMLElement;

  /**
   * Scroll axis. `horizontal` keeps content on one line and scrolls sideways;
   * `vertical` wraps content and scrolls within a capped height (`--max-block-size`).
   */
  @property({ reflect: true }) orientation: 'horizontal' | 'vertical' = 'horizontal';

  /** Removes the fading edge cue while keeping the content scrollable. */
  @property({ attribute: 'without-shadow', type: Boolean, reflect: true }) withoutShadow = false;

  /**
   * Hides the scrollbar but keeps scrolling. Accessibility trade-off: the
   * region stays keyboard-scrollable, but the visible scroll affordance is gone.
   */
  @property({ attribute: 'without-scrollbar', type: Boolean, reflect: true }) withoutScrollbar = false;

  /**
   * Disables mouse-wheel scrolling of the content. Only relevant alongside
   * `without-scrollbar` (which otherwise translates the wheel into horizontal
   * scrolling): with this set the wheel scrolls the page as usual and the
   * content is panned by click-drag instead.
   */
  @property({ attribute: 'without-wheel', type: Boolean, reflect: true }) withoutWheel = false;

  /** True once the drag/wheel interaction listeners have been attached (lazily, on first overflow). */
  private interactionAttached = false;
  private observedContainer?: HTMLElement;

  // Drag-to-scroll state. Only engaged for a mouse (touch and pen already
  // scroll the region natively, so hijacking their gestures would double-scroll).
  private isDragging = false;
  private dragMoved = false;
  private suppressNextClick = false;
  private dragStart = { x: 0, y: 0, left: 0, top: 0 };

  connectedCallback() {
    super.connectedCallback();
    this.updateComplete.then(() => {
      // The element may have been disconnected before this microtask resolved
      // (rapid table re-render/virtualization). Don't observe a detached node -
      // there is no future disconnectedCallback to clean it up.
      if (!this.isConnected) {
        return;
      }
      const el = this.scrollContainer;
      this.observedContainer = el;
      fadeByContainer.set(el, this);
      // Re-measure whenever the container's box changes (responsive shrink,
      // column resize). Content-driven overflow is picked up via slotchange.
      sharedResizeObserver?.observe(el);
      // Interaction listeners are attached lazily (only once the region actually
      // overflows - see applyEdges), so non-overflowing cells stay cheap.
      scheduleMeasure(this);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    measureQueue.delete(this);
    const el = this.observedContainer;
    if (el) {
      sharedResizeObserver?.unobserve(el);
      fadeByContainer.delete(el);
      this.detachInteraction(el);
    }
    this.observedContainer = undefined;
  }

  // Orientation changes (and any content-driven re-render) alter the overflow
  // geometry, so re-evaluate after the DOM has updated - off the hot path.
  protected updated() {
    scheduleMeasure(this);
  }

  private handleScroll = () => this.applyEdges(this.readMetrics());
  private handleSlotChange = () => scheduleMeasure(this);

  /**
   * Translates a vertical mouse wheel into horizontal movement while the
   * scrollbar is hidden, so a mouse can still scroll a horizontal region. Only
   * consumes the event while there is room to move that way, so the page keeps
   * scrolling once an edge is reached. Vertical regions scroll with the wheel
   * natively and are left untouched.
   */
  private handleWheel = (e: WheelEvent) => {
    if (this.withoutWheel || !this.withoutScrollbar || this.orientation !== 'horizontal') {
      return;
    }

    const el = this.scrollContainer;
    // A mouse wheel reports vertical delta; honour deltaX too so a trackpad's
    // horizontal gesture is not doubled.
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (delta === 0) {
      return;
    }

    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    if ((delta < 0 && atStart) || (delta > 0 && atEnd)) {
      return;
    }

    el.scrollLeft += delta;
    e.preventDefault();
  };

  private handlePointerDown = (e: PointerEvent) => {
    this.suppressNextClick = false;
    // Mouse only, primary button, and only when the bar is hidden (otherwise
    // the user has the scrollbar and drag would fight text selection).
    if (!this.withoutScrollbar || e.pointerType !== 'mouse' || e.button !== 0) {
      return;
    }

    const el = this.scrollContainer;
    const canScroll =
      this.orientation === 'vertical' ? el.scrollHeight - el.clientHeight > 1 : el.scrollWidth - el.clientWidth > 1;
    if (!canScroll) {
      return;
    }

    this.isDragging = true;
    this.dragMoved = false;
    this.dragStart = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    // NB: the pointer is deliberately NOT captured here. Capturing on
    // pointerdown makes Chromium retarget the pointer's trailing `click` to
    // this container, so a plain click on a link or button inside an
    // overflowing region never reaches it - the cell looks unclickable.
    // Capture is taken lazily once an actual drag starts (handlePointerMove),
    // by which point the terminating click is being suppressed anyway.
  };

  private handlePointerMove = (e: PointerEvent) => {
    if (!this.isDragging) {
      return;
    }

    const el = this.scrollContainer;
    const dx = e.clientX - this.dragStart.x;
    const dy = e.clientY - this.dragStart.y;

    // Ignore micro-movement so a plain click is not mistaken for a drag.
    if (!this.dragMoved && Math.hypot(dx, dy) <= 3) {
      return;
    }

    if (!this.dragMoved) {
      this.dragMoved = true;
      el.classList.add('is-dragging');
      window.getSelection()?.removeAllRanges();
      try {
        // Now that this is a genuine drag, capture the pointer so the gesture
        // survives it leaving the element. Best-effort: a failure here must
        // not abort the drag. Safe against the click-retarget issue above
        // because a moved drag suppresses its terminating click.
        el.setPointerCapture(e.pointerId);
      } catch {
        /* no active pointer to capture */
      }
    }

    if (this.orientation === 'vertical') {
      el.scrollTop = this.dragStart.top - dy;
    } else {
      el.scrollLeft = this.dragStart.left - dx;
    }
  };

  private handlePointerUp = (e: PointerEvent) => {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;
    const el = this.scrollContainer;
    el.classList.remove('is-dragging');
    if (el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }

    // A drag ends with a click; swallow that one click so a pan cannot activate
    // a link or button inside the content.
    if (this.dragMoved) {
      this.suppressNextClick = true;
    }
  };

  private handleClick = (e: MouseEvent) => {
    if (this.suppressNextClick) {
      e.preventDefault();
      e.stopPropagation();
      this.suppressNextClick = false;
    }
  };

  /**
   * Reads the scroll geometry for this region. Pure read (no DOM writes) so the
   * batched measurement pass can read every queued fade before writing any of
   * them, keeping the whole batch to a single reflow.
   */
  readMetrics(): FadeMetrics | null {
    const el = this.scrollContainer;
    if (!el) {
      return null;
    }
    const vertical = this.orientation === 'vertical';
    return {
      scrollPos: vertical ? el.scrollTop : el.scrollLeft,
      viewport: vertical ? el.clientHeight : el.clientWidth,
      content: vertical ? el.scrollHeight : el.scrollWidth
    };
  }

  /**
   * Applies the fade cue + keyboard affordance from a previously read snapshot.
   * Pure write (no layout reads). Also lazily attaches the drag/wheel listeners
   * the first time the region is found to overflow.
   */
  applyEdges(metrics: FadeMetrics | null) {
    const el = this.scrollContainer;
    if (!el || !metrics) {
      return;
    }

    // 1px tolerance absorbs sub-pixel rounding so an at-rest edge does not flicker.
    const startFaded = metrics.scrollPos > 1;
    const endFaded = metrics.scrollPos + metrics.viewport < metrics.content - 1;
    const overflowing = metrics.content - metrics.viewport > 1;

    // Attach interaction before flipping is-scrollable so anything waiting on
    // that class (tests, callers) sees a fully wired region.
    if (overflowing) {
      this.attachInteraction(el);
    }

    el.classList.toggle('is-start', startFaded);
    el.classList.toggle('is-end', endFaded);
    el.classList.toggle('is-scrollable', overflowing);

    // Keep the scroll region reachable by keyboard only while it can scroll, so
    // non-overflowing content does not add a stray tab stop.
    if (overflowing) {
      el.setAttribute('tabindex', '0');
    } else {
      el.removeAttribute('tabindex');
    }
  }

  private attachInteraction(el: HTMLElement) {
    if (this.interactionAttached) {
      return;
    }
    this.interactionAttached = true;
    // Restore mouse scrolling when the scrollbar is hidden: a horizontal region
    // ignores the (vertical) wheel, and no scroll container drag-pans natively.
    // Wheel is bound non-passive so it can preventDefault the page.
    el.addEventListener('wheel', this.handleWheel, { passive: false });
    el.addEventListener('pointerdown', this.handlePointerDown);
    el.addEventListener('pointermove', this.handlePointerMove);
    el.addEventListener('pointerup', this.handlePointerUp);
    el.addEventListener('pointercancel', this.handlePointerUp);
    el.addEventListener('click', this.handleClick, { capture: true });
  }

  private detachInteraction(el: HTMLElement) {
    if (!this.interactionAttached) {
      return;
    }
    this.interactionAttached = false;
    el.removeEventListener('wheel', this.handleWheel);
    el.removeEventListener('pointerdown', this.handlePointerDown);
    el.removeEventListener('pointermove', this.handlePointerMove);
    el.removeEventListener('pointerup', this.handlePointerUp);
    el.removeEventListener('pointercancel', this.handlePointerUp);
    el.removeEventListener('click', this.handleClick, { capture: true });
  }

  render() {
    return html`
      <div part="base" class="fade" @scroll=${this.handleScroll}>
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-fade': WebmoduleFade;
  }
}
